// Excel (.xlsx / .xls) parser for the Monk Mackenzie forecasting workbook.
//
// Reads the firm's natural sheet layout:
//   • A header row containing: Number, Project, Project Principal, Scope,
//     Contract, Notes, Prob., then one column per month (May..April).
//   • Project blocks underneath — the first row of each project carries
//     Number/Title/Principal/Contract (and is the SubTotal row).
//     Subsequent rows are either:
//        - SCOPE rows  → label is a stage name, probability column has a value,
//          and month columns hold $ amounts.
//        - PERSON rows → label is a person name, probability blank, month columns
//          hold percentages (0.4 OR 40%).
//   • Rows whose Scope cell reads "SubTotal" are skipped (we recompute totals).
//
// Output matches the dataset shape consumed by /api/data (PUT).

import * as XLSX from "xlsx";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const MONTH_TOKENS = new Set([
  "jan","january","feb","february","mar","march","apr","april",
  "may","jun","june","jul","july","aug","august",
  "sep","sept","september","oct","october","nov","november","dec","december",
]);

const CANONICAL_MONTH = {
  jan: "January", january: "January",
  feb: "February", february: "February",
  mar: "March", march: "March",
  apr: "April", april: "April",
  may: "May",
  jun: "June", june: "June",
  jul: "July", july: "July",
  aug: "August", august: "August",
  sep: "September", sept: "September", september: "September",
  oct: "October", october: "October",
  nov: "November", november: "November",
  dec: "December", december: "December",
};

const CANONICAL_STAGES = [
  "Feasibility", "Concept", "Preliminary Design",
  "Developed Design", "Detailed Design", "Procurement", "Site Observation",
];
const STAGE_KEYWORDS = ["feasibility","concept","preliminary","developed","detailed","procurement","observation","schematic"];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const norm = (s) => String(s ?? "").trim();
const lower = (s) => norm(s).toLowerCase();

const slug = (s) =>
  norm(s).toLowerCase().normalize("NFKD").replace(/[^\w\s-]/g, "").trim()
    .replace(/\s+/g, "_").replace(/_+/g, "_");

// Money: tolerate "$15,000", "15000.5", "—"
const parseMoney = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const cleaned = String(v).replace(/[$,]/g, "").trim();
  if (!cleaned || cleaned === "—" || cleaned === "-") return 0;
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
};

// Load: 0.4, "40%", "40", "0.4" all → 0.4
const parseLoad = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number") {
    if (!Number.isFinite(v)) return 0;
    return v > 1.5 ? v / 100 : v; // Excel may store percent format as 40 or 0.4
  }
  const txt = String(v).trim();
  if (!txt || txt === "—" || txt === "-") return 0;
  if (txt.endsWith("%")) return parseFloat(txt.slice(0, -1)) / 100;
  const n = parseFloat(txt.replace(/,/g, ""));
  if (!Number.isFinite(n)) return 0;
  return n > 1.5 ? n / 100 : n;
};

// Probability: "100%", "70%", 0.7, 1.0
const parseProb = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") {
    if (!Number.isFinite(v)) return null;
    return v > 1.5 ? v / 100 : v;
  }
  const txt = String(v).trim();
  if (!txt || txt === "—" || txt === "-") return null;
  if (txt.endsWith("%")) return parseFloat(txt.slice(0, -1)) / 100;
  const n = parseFloat(txt);
  if (!Number.isFinite(n)) return null;
  return n > 1.5 ? n / 100 : n;
};

// ---------------------------------------------------------------------------
// Header detection
// ---------------------------------------------------------------------------
function findHeaderRow(aoa) {
  // Look for a row that mentions Number/Project and ≥3 month tokens.
  for (let i = 0; i < aoa.length; i += 1) {
    const row = aoa[i] || [];
    const cells = row.map(lower);
    const hasNumber  = cells.some((c) => c === "number" || c === "project id" || c === "project number" || c === "no.");
    const hasProject = cells.some((c) => c === "project" || c === "project name" || c === "title");
    const monthHits  = cells.filter((c) => MONTH_TOKENS.has(c)).length;
    if ((hasNumber || hasProject) && monthHits >= 3) return i;
  }
  return -1;
}

function mapColumns(header) {
  const lc = header.map(lower);
  const findExact = (...names) => {
    for (const n of names) {
      const i = lc.indexOf(n);
      if (i !== -1) return i;
    }
    return -1;
  };
  const map = {
    number:    findExact("number", "no.", "project id", "project number"),
    title:     findExact("project", "project name", "title"),
    principal: findExact("project principal", "principal"),
    scope:     findExact("scope", "stage", "label"),
    contract:  findExact("contract", "contract status"),
    notes:     findExact("notes", "note"),
    prob:      findExact("prob.", "prob", "probability"),
  };

  // Month columns: every cell whose lower-case text is a recognised month token.
  const months = [];
  lc.forEach((c, i) => {
    if (MONTH_TOKENS.has(c)) months.push({ index: i, name: CANONICAL_MONTH[c] });
  });
  // Sort by their column index (workbook order) — matches the firm's fiscal year.
  months.sort((a, b) => a.index - b.index);

  return { ...map, months };
}

// ---------------------------------------------------------------------------
// Row classification
// ---------------------------------------------------------------------------
const isStageLabel = (label) => {
  const l = lower(label);
  if (!l) return false;
  return STAGE_KEYWORDS.some((kw) => l.includes(kw))
    || CANONICAL_STAGES.some((s) => lower(s) === l);
};

const normaliseStage = (label) => {
  const l = lower(label);
  for (const s of CANONICAL_STAGES) {
    if (lower(s) === l) return s;
  }
  // partial match
  if (l.includes("feasibility")) return "Feasibility";
  if (l.includes("concept"))     return "Concept";
  if (l.includes("preliminary")) return "Preliminary Design";
  if (l.includes("developed"))   return "Developed Design";
  if (l.includes("detailed"))    return "Detailed Design";
  if (l.includes("procurement")) return "Procurement";
  if (l.includes("observation")) return "Site Observation";
  if (l.includes("schematic"))   return "Concept";
  // Fall back to the raw label if it doesn't map cleanly.
  return norm(label);
};

// Decide whether a non-header row is SCOPE or PERSON.
function classifyRow(row, cols) {
  const scopeLabel = norm(row[cols.scope]);
  const probVal = parseProb(row[cols.prob]);
  const monthVals = cols.months.map((m) => row[m.index]);
  const numericVals = monthVals
    .map((v) => (typeof v === "number" ? v : parseFloat(String(v).replace(/[$,%]/g, ""))))
    .filter((n) => Number.isFinite(n));
  const maxAbs = numericVals.length ? Math.max(...numericVals.map(Math.abs)) : 0;

  // Strong signals first
  if (lower(scopeLabel) === "subtotal") return "subtotal";
  if (isStageLabel(scopeLabel))         return "scope";
  if (probVal !== null && probVal > 0)  return "scope";

  // Numerical fallback
  if (maxAbs >= 5)   return "scope";   // a $ value
  if (maxAbs > 0)    return "person";  // a fractional / percentage value
  return "empty";
}

// ---------------------------------------------------------------------------
// Main entry
// ---------------------------------------------------------------------------
export function xlsxArrayBufferToAoA(arrayBuffer, sheetName) {
  const wb = XLSX.read(arrayBuffer, { type: "array" });
  const name = sheetName || wb.SheetNames[0];
  const sheet = wb.Sheets[name];
  if (!sheet) throw new Error(`Sheet "${name}" not found.`);
  return XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: "" });
}

export function aoaToDataset(aoa, opts = {}) {
  const existing = opts.existing || {};
  const headerIdx = findHeaderRow(aoa);
  if (headerIdx === -1) {
    throw new Error('Could not find a header row. Make sure your sheet has a "Number" column and month columns (May, June, …).');
  }
  const header = aoa[headerIdx];
  const cols = mapColumns(header);
  if (cols.months.length === 0) throw new Error("No month columns detected in the header row.");
  if (cols.scope === -1) throw new Error('Missing "Scope" column.');

  // Carry forward existing principals/people so we can match by name first.
  const existingPrincipalsByName = Object.fromEntries(
    (existing.principals || []).map((p) => [lower(p.name), p])
  );
  const existingPeopleByName = Object.fromEntries(
    (existing.people || []).map((p) => [lower(p.name), p])
  );
  const principalsOut = [...(existing.principals || [])];
  const peopleOut     = [...(existing.people || [])];

  const ensurePrincipal = (name) => {
    const n = norm(name);
    if (!n) return null;
    const found = existingPrincipalsByName[lower(n)];
    if (found) return found.id;
    const id = slug(n);
    if (!principalsOut.find((p) => p.id === id)) {
      principalsOut.push({ id, name: n });
      existingPrincipalsByName[lower(n)] = { id, name: n };
    }
    return id;
  };

  const ensurePerson = (name) => {
    const n = norm(name);
    if (!n) return null;
    const found = existingPeopleByName[lower(n)];
    if (found) return found.id;
    let id = slug(n);
    let bump = 1;
    while (peopleOut.find((p) => p.id === id)) { bump += 1; id = `${slug(n)}_${bump}`; }
    const person = { id, name: n, fullTimeCapacity: 1.0 };
    peopleOut.push(person);
    existingPeopleByName[lower(n)] = person;
    return id;
  };

  const months = cols.months.map((m) => m.name);
  const projects = [];
  const projectsByPid = new Map();
  let current = null;

  const debug = { rows: 0, scopeRows: 0, personRows: 0, skipped: 0, projects: 0 };

  for (let r = headerIdx + 1; r < aoa.length; r += 1) {
    const row = aoa[r] || [];
    if (row.every((c) => c === "" || c === null || c === undefined)) continue;
    debug.rows += 1;

    const number = cols.number !== -1 ? norm(row[cols.number]) : "";
    if (number) {
      // Start a new project (the first row carries identity + subtotal).
      const pid = number;
      const principalId = cols.principal !== -1 ? ensurePrincipal(row[cols.principal]) : null;
      const title    = cols.title    !== -1 ? norm(row[cols.title])    : pid;
      const contract = cols.contract !== -1 ? norm(row[cols.contract]) : "Yes";
      const note     = cols.notes    !== -1 ? norm(row[cols.notes])    : "";
      current = projectsByPid.get(pid) || {
        id: pid,
        name: title || pid,
        principalId,
        contract: contract || "Yes",
        note,
        scopes: [],
        allocations: [],
      };
      // First row of a project is also the SubTotal row in the firm's sheet —
      // skip its financial values, we'll recompute the subtotal from scope rows.
      if (!projectsByPid.has(pid)) {
        projectsByPid.set(pid, current);
        projects.push(current);
        debug.projects += 1;
      }
      continue;
    }

    if (!current) { debug.skipped += 1; continue; }

    const kind = classifyRow(row, cols);
    if (kind === "subtotal" || kind === "empty") { debug.skipped += 1; continue; }

    if (kind === "scope") {
      const label = norm(row[cols.scope]);
      const stage = normaliseStage(label);
      const probability = (() => {
        const p = parseProb(row[cols.prob]);
        return p === null ? 1.0 : p;
      })();
      const revenue = {};
      cols.months.forEach((m) => { revenue[m.name] = parseMoney(row[m.index]); });
      current.scopes.push({
        id: `${current.id}_s${current.scopes.length + 1}`,
        stage: stage || label || "Scope",
        probability,
        revenue,
      });
      debug.scopeRows += 1;
    } else if (kind === "person") {
      const personId = ensurePerson(row[cols.scope]);
      if (!personId) { debug.skipped += 1; continue; }
      const load = {};
      cols.months.forEach((m) => { load[m.name] = parseLoad(row[m.index]); });
      current.allocations.push({ personId, load });
      debug.personRows += 1;
    }
  }

  const baseMeta = existing.meta || {};
  return {
    dataset: {
      meta: {
        firm: baseMeta.firm || "Monk Mackenzie",
        reportDate: baseMeta.reportDate || new Date().toISOString().slice(0, 10),
        currency: baseMeta.currency || "NZD",
        hoursPerFte: baseMeta.hoursPerFte || 40,
        stages: baseMeta.stages || CANONICAL_STAGES,
        months,
      },
      people: peopleOut,
      principals: principalsOut,
      projects,
    },
    debug,
  };
}

export function xlsxToDataset(arrayBuffer, opts = {}) {
  const aoa = xlsxArrayBufferToAoA(arrayBuffer, opts.sheetName);
  return aoaToDataset(aoa, opts);
}
